/**
 * Utility functions for normalizing screen contexts
 * 
 * This module provides functions to normalize screen contexts from various
 * pages to a standard format for the specialized questions page.
 */

/**
 * Normalizes a screen context to a standard identifier
 * 
 * @param context - The raw screen context string
 * @returns Normalized context identifier (e.g., 'CTEs', 'NFEs', 'Clients', etc.)
 */
export const normalizeScreenContext = (context: string): string => {
  if (!context || context.trim().length === 0) {
    return 'Specialized Questions'
  }

  const contextLower = context.toLowerCase().trim()

  // CTEs - Conhecimentos de Transporte Eletrônico
  if (
    contextLower.includes('cte') ||
    contextLower.includes('conhecimento') ||
    contextLower.includes('conhecimentos de transporte')
  ) {
    return 'CTEs'
  }

  // NFEs - Notas Fiscais Eletrônicas
  if (
    contextLower.includes('nfe') ||
    contextLower.includes('nota fiscal') ||
    contextLower.includes('notas fiscais')
  ) {
    return 'NFEs'
  }

  // Clients - Clientes
  if (
    contextLower.includes('client') ||
    contextLower.includes('cliente') ||
    contextLower.includes('carteira de clientes') ||
    contextLower.includes('visão geral - carteira')
  ) {
    return 'Clients'
  }

  // Dashboard - Removido pois Dashboard não acessa perguntas especializadas
  // if (
  //   contextLower.includes('dashboard') ||
  //   contextLower.includes('visão geral')
  // ) {
  //   return 'Dashboard'
  // }

  // Logistics - Logística
  if (
    contextLower.includes('logistic') ||
    contextLower.includes('logística') ||
    contextLower.includes('logistica')
  ) {
    return 'Logistics'
  }

  // Freight - Fretes
  if (
    contextLower.includes('freight') ||
    contextLower.includes('frete') ||
    contextLower.includes('fretes')
  ) {
    return 'Freight'
  }

  // Vehicle Freight - Fretes por Veículo
  if (
    contextLower.includes('vehicle') ||
    contextLower.includes('veículo') ||
    contextLower.includes('veiculo') ||
    contextLower.includes('fretes por veículo') ||
    contextLower.includes('fretes por veiculo')
  ) {
    return 'Vehicles'
  }

  // Distribution Centers - Centros de Distribuição
  if (
    contextLower.includes('centro de distribuição') ||
    contextLower.includes('centro de distribuicao') ||
    contextLower.includes('centros de distribuição') ||
    contextLower.includes('centros de distribuicao') ||
    contextLower.includes('cd') ||
    contextLower.includes('distribution center')
  ) {
    return 'DistributionCenters'
  }

  // Costs - Custos
  if (
    contextLower.includes('cost') ||
    contextLower.includes('custo') ||
    contextLower.includes('custos') ||
    contextLower.includes('tendências de custos') ||
    contextLower.includes('tendencias de custos')
  ) {
    return 'Costs'
  }

  // Sales Force - Força de Vendas
  if (
    contextLower.includes('sales force') ||
    contextLower.includes('força de venda') ||
    contextLower.includes('forca de venda') ||
    contextLower.includes('força de vendas') ||
    contextLower.includes('forca de vendas') ||
    contextLower.includes('vendas')
  ) {
    return 'SalesForce'
  }

  // Se não encontrar palavra-chave conhecida, retornar o contexto original (limitado a 50 caracteres)
  if (context.length <= 50) {
    return context
  }

  // Se for muito longo, usar primeiras palavras
  const words = context.split(/\s+/).slice(0, 3).join(' ')
  return words || 'Specialized Questions'
}

/**
 * Gets a friendly display name for a normalized context
 * 
 * @param normalizedContext - The normalized context identifier
 * @returns Friendly display name in Portuguese
 */
export const getContextDisplayName = (normalizedContext: string): string => {
  const contextMap: Record<string, string> = {
    'CTEs': 'CTEs',
    'NFEs': 'NFEs',
    'Clients': 'Clientes',
    'Logistics': 'Logística',
    'Freight': 'Fretes',
    'Vehicles': 'Veículos',
    'DistributionCenters': 'Centros de Distribuição',
    'Costs': 'Custos',
    'SalesForce': 'Força de Vendas',
    'Specialized Questions': 'Geral'
  }

  return contextMap[normalizedContext] || normalizedContext
}

